import { useEffect } from 'react';
import { Page } from './Page';
import { StatLabel } from '../components/common';
import { useNav } from './NavContext';
import { FinancesTrendChart } from '../components/finances/FinancesTrendChart';
import { FinancesDonut } from '../components/finances/FinancesDonut';
import { FinYoY } from '../components/finances/FinancesBars';
import { FinancesSkeleton } from '../components/finances/FinancesSkeleton';
import { useFinancesData } from '../components/finances/useFinancesData';
import {
  ANCHOR_MONTH, fmtEur, headline, monthLabel, trendPoints, yoyByMonth,
} from '../components/finances/financesData';

function Delta({ ratio }: { ratio: number | null }) {
  if (ratio == null) return <span className="fin-delta flat">—</span>;
  const up = ratio >= 0;
  return <span className={'fin-delta ' + (up ? 'up' : 'down')}>{up ? '▲' : '▼'} {Math.abs(ratio * 100).toFixed(1)}%</span>;
}

// Finances — revenue analytics. The header year-stepper drives the whole page.
//
// ONE figure, and it is revenue EARNED: each stay's value recognised across the
// nights it actually served, not landed whole in its check-in month. That single
// change is what stopped July 2026 reporting €13,710 of August's nights.
//
// There is no basis switch. A Received/Billed toggle was built here and removed
// before it shipped (Freddi, 2026-07-30): two revenue numbers on one page ask
// the reader to know which question they are asking before they can read either.
// The payments ledger still records when money arrives — see the booking modal
// and the Daily desk — it simply is not reported here.
export function FinancesPage() {
  const fin = useFinancesData();
  // The shown year is read off the app's shared anchor. The top-bar picker runs in
  // YEAR mode on Finances (see datePickerMode), so stepping it moves one year.
  const { anchor, setAnchor } = useNav();

  // Keep the picker inside the covered range: if the anchor year is outside the
  // data, snap it back so the header can never show a year the charts don't. Runs
  // only when the anchor lands out of range, so it settles in one pass (no loop).
  useEffect(() => {
    if (!fin.data) return;
    const ys = [...new Set(fin.data.series.map((r) => r.year))];
    const clamped = Math.min(Math.max(anchor.getFullYear(), ys[0]!), ys[ys.length - 1]!);
    if (clamped !== anchor.getFullYear()) setAnchor(new Date(clamped, 0, 1));
  }, [fin.data, anchor, setAnchor]);

  if (!fin.data) {
    return (
      <Page title="Finances">
        <div className="finances-view"><FinancesSkeleton /></div>
      </Page>
    );
  }

  const { series, meta } = fin.data;
  const years = [...new Set(series.map((r) => r.year))];
  const firstYear = years[0]!;
  const latestYear = years[years.length - 1]!;
  // Read the anchor's YEAR, clamped to the covered range (belt-and-braces with the
  // write-back effect above, so the charts are safe even before it settles).
  const sel = Math.min(Math.max(anchor.getFullYear(), firstYear), latestYear);

  const focusRows = series.filter((r) => r.year === sel);
  const earnedRows = focusRows.filter((r) => !r.forward);
  const h = headline(series, sel, sel);
  const points = trendPoints(series, sel, 'month');
  const yoy = yoyByMonth(series, sel);

  const weekEarned = meta.weekByYear?.[sel] ?? 0;
  const focusMonth = focusRows.find((r) => r.month === ANCHOR_MONTH);
  const monthEarned = focusMonth?.booked ?? 0;
  const yearEarned = h.revenue;
  // Projected from BOOKINGS, not a trend: everything already on the books for the
  // year — earned to date plus the committed forward book.
  const bookedYear = focusRows.reduce((a, r) => a + r.booked, 0);

  const area = earnedRows.reduce((o, r) => ({ big: o.big + r.area.big, little: o.little + r.area.little, mix: o.mix + r.area.mix }), { big: 0, little: 0, mix: 0 });
  const newRev = earnedRows.reduce((a, r) => a + r.newRev, 0);
  const repeatRev = earnedRows.reduce((a, r) => a + r.repeatRev, 0);
  const ytd = h.hasPartial ? ' YTD' : '';

  return (
    <Page title="Finances">
      <div className="finances-view">
        <div className="bw-stats fin-stats">
          <div className="bw-stat">
            <StatLabel icon="calendar">Earned this week</StatLabel>
            <div className="val mono">{fmtEur(weekEarned)}</div>
            <div className="fin-stat-sub">week of {monthLabel(ANCHOR_MONTH)}</div>
          </div>
          <div className="bw-stat">
            <StatLabel icon="calendar-today">Earned in {monthLabel(ANCHOR_MONTH)}</StatLabel>
            <div className="val mono">{fmtEur(monthEarned)}</div>
            <div className="fin-stat-sub">{h.hasPartial ? 'the month so far' : 'that month'}</div>
          </div>
          <div className="bw-stat prime">
            <StatLabel icon="bar">Earned this year</StatLabel>
            <div className="val mono">{fmtEur(yearEarned)}</div>
            <div className="fin-stat-sub">
              {h.hasPartial ? 'year to date' : (h.hasPrevYear ? <><Delta ratio={h.yoy} /> vs {sel - 1}</> : 'first year of data')}
            </div>
          </div>
          {/* The forward book — the natural counterpart to earned-to-date.
              COMMITTED, not served: it is the one figure on this page that is
              not revenue, and it says so rather than adding itself to the
              headline. */}
          <div className="bw-stat">
            <StatLabel icon="wallet">{h.hasPartial ? 'Projected this year' : `Total ${sel}`}</StatLabel>
            <div className="val mono">{fmtEur(bookedYear)}</div>
            <div className="fin-stat-sub">{h.hasPartial ? 'revenue on the books' : 'full year'}</div>
          </div>
        </div>

        <div className="fin-trend">
          <FinancesTrendChart points={points} wagesEstimated={meta.wagesEstimated} />
        </div>

        <div className="fin-grid-2">
          <div className="fin-panel">
            <div className="fin-panel-head">
              <h3>{sel} vs {sel - 1}</h3>
              {h.hasPrevYear
                ? <div className="chart-legend"><span className="chart-legend-item"><span className="sw" style={{ background: 'var(--border-strong)' }} /> {sel - 1}</span><span className="chart-legend-item"><span className="sw" style={{ background: 'var(--brand)' }} /> {sel}</span></div>
                : null}
            </div>
            {h.hasPrevYear
              ? <FinYoY months={yoy} prevYear={sel - 1} curYear={sel} />
              : <div className="bookings-empty"><div className="be-inner"><p>{sel} is the first year of data — year-on-year unlocks once {sel + 1} begins.</p></div></div>}
          </div>

          <div className="fin-panel">
            <div className="fin-panel-head">
              <h3>Where {sel}{ytd} earned revenue comes from</h3>
            </div>
            <div className="fin-sources">
              <FinancesDonut
                title="By kennel area"
                segments={[
                  { label: 'Big', value: area.big, color: 'var(--brand)' },
                  { label: 'Little', value: area.little, color: 'var(--brand-deep)' },
                  { label: 'Mix', value: area.mix, color: 'var(--secondary)' },
                ]}
              />
              <FinancesDonut
                title="New vs repeat customers"
                segments={[
                  { label: 'Repeat', value: repeatRev, color: 'var(--brand)' },
                  { label: 'New', value: newRev, color: 'var(--secondary)' },
                ]}
              />
            </div>
            <div className="fin-cancel-note">
              <span className="fin-cancel-dot" />
              Lost to cancellations · {sel}{ytd}: <strong>{fmtEur(h.cancelled)}</strong>
              <span className="fin-cancel-sub"> — not counted in earned revenue above</span>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
